
import { defineComponent } from 'vue';
import { mapState } from 'pinia';
import * as Highcharts from 'highcharts';
import { useStore } from '@/plugins/store/index';
import CustomSelect from '@/components/ui/CustomSelect.vue';
import GenericChart from '@/components/charts/GenericChart.vue';
import { AllFilterSelectors, CourseStatistics, TimePeriod } from '@/types';
import { roundIfNecessary } from '@/util/helpers/numbers';
import {
    getAllEventsGendersConfig,
    getAllEventsWithHelpConfig,
    getEndedEventsGendersConfig,
    getEndedEventsWithHelpConfig,
    getAllEventsAgeRangeConfig,
    getEndedEventsAgeRangeConfig,
    getAllEventsRegionsConfig,
    getEndedEventsRegionsConfig,
    getRegionsColumnConfig,
    getDevelopmentConfig,
    getAccumulatedDevelopmentConfig,
} from '@/util/charts';

export default defineComponent({
    components: { CustomSelect, GenericChart },
    props: {
        includeRegionCharts: {
            type: Boolean,
            default: true,
        },
    },
    methods: {
        isNaN(number: any) {
            return Number.isNaN(number);
        },
        getSelectedPeriodDates(courseStatistics: CourseStatistics[], selectedPeriod: null | { text: string; id: TimePeriod | AllFilterSelectors }) {
            const minDateFromCourseStatistics = new Date(Math.min(...courseStatistics.map((courseStatistic) => new Date(courseStatistic.started).getTime())));
            switch (selectedPeriod?.id) {
                case TimePeriod.Always: {
                    return {
                        from: minDateFromCourseStatistics,
                        to: new Date(),
                    };
                }
                case TimePeriod.CustomRange: {
                    if (this.customDateRange?.from && this.customDateRange?.to) {
                        const customFrom = new Date(this.customDateRange.from);
                        const customTo = new Date(this.customDateRange.to);
                        if (!isNaN(customFrom.getTime()) && !isNaN(customTo.getTime()) && customTo.getTime() >= customFrom.getTime()) {
                            return {
                                from: customFrom,
                                to: customTo,
                            };
                        }
                    }
                    return {
                        from: minDateFromCourseStatistics,
                        to: new Date(),
                    };
                }
                case TimePeriod.LastYear: {
                    let lastYear = new Date();
                    lastYear = new Date(Math.min(lastYear.setFullYear(lastYear.getFullYear() - 1), minDateFromCourseStatistics.getTime()));
                    return {
                        from: lastYear,
                        to: new Date(),
                    };
                }
                case TimePeriod.LastThirtyDays: {
                    let lastThirtyDays = new Date();
                    lastThirtyDays = new Date(Math.min(lastThirtyDays.setDate(lastThirtyDays.getDate() - 30), minDateFromCourseStatistics.getTime()));
                    return {
                        from: lastThirtyDays,
                        to: new Date(),
                    };
                }
                case TimePeriod.LastSevenDays: {
                    let lastSevenDays = new Date();
                    lastSevenDays = new Date(Math.min(lastSevenDays.setDate(lastSevenDays.getDate() - 7), minDateFromCourseStatistics.getTime()));
                    return {
                        from: lastSevenDays,
                        to: new Date(),
                    };
                }
                default: {
                    return {
                        from: minDateFromCourseStatistics,
                        to: new Date(),
                    };
                }
            }
        },
    },
    computed: {
        ...mapState(useStore, ['courseStatistics', 'ageRanges', 'regions', 'selectedRegions', 'selectedPeriod', 'partnerRegionsSelected', 'customDateRange']),
        endedCourseStatistics(): CourseStatistics[] {
            return this.courseStatistics.filter((currentCourseStatistic) => currentCourseStatistic.ended);
        },
        percentageOfCourseStatisticsHasEnded(): number {
            return roundIfNecessary((this.endedCourseStatistics.length / this.courseStatistics.length) * 100, 1);
        },
        regionsColumnChart(): any {
            if (!this.courseStatistics.length || this.selectedRegions.length === 1) {
                return null;
            }

            return getRegionsColumnConfig(this.regions, this.selectedRegions, this.courseStatistics);
        },
        developmentChart(): any {
            if (!this.courseStatistics.length) {
                return null;
            }

            const selectedPeriodDates = this.getSelectedPeriodDates(this.courseStatistics, this.selectedPeriod);

            return getDevelopmentConfig(this.courseStatistics, selectedPeriodDates.from, selectedPeriodDates.to, this.partnerRegionsSelected, this.selectedRegions);
        },
        accumulatedDevelopmentChart(): any {
            if (!this.courseStatistics.length) {
                return null;
            }
            const selectedPeriodDates = this.getSelectedPeriodDates(this.courseStatistics, this.selectedPeriod);

            return getAccumulatedDevelopmentConfig(this.courseStatistics, selectedPeriodDates.from, selectedPeriodDates.to, this.partnerRegionsSelected, this.selectedRegions);
        },
        charts(): any[] {
            if (!this.courseStatistics.length) {
                return [];
            }
            const numberOfCourseStatisticEvents = this.courseStatistics.length;
            const numberOfEndedCourseStatisticEvents = this.courseStatistics.filter((currentCourseStatistic) => currentCourseStatistic.ended).length;
            const allChartsStartedWithHelp = this.courseStatistics.filter((currentCourseStatistic) => currentCourseStatistic.withHelp).length;
            const endedChartsStartedWithHelp = this.endedCourseStatistics.filter((currentCourseStatistic) => currentCourseStatistic.withHelp).length;

            const result = [
                getAllEventsWithHelpConfig('ALL_EVENTS_WITH_HELP', 0, allChartsStartedWithHelp, numberOfCourseStatisticEvents),
                getEndedEventsWithHelpConfig('ENDED_EVENTS_WITH_HELP', 0, endedChartsStartedWithHelp, numberOfEndedCourseStatisticEvents),
                getAllEventsGendersConfig('ALL_EVENTS_GENDERS', 1, this.courseStatistics),
                getEndedEventsGendersConfig('ENDED_EVENTS_GENDERS', 1, this.endedCourseStatistics),
                getAllEventsAgeRangeConfig('ALL_EVENTS_AGE_RANGE', 2, this.ageRanges, this.courseStatistics),
                getEndedEventsAgeRangeConfig('ENDED_EVENTS_AGE_RANGE', 2, this.ageRanges, this.endedCourseStatistics),
            ] as Highcharts.Options[];

            if (this.$props.includeRegionCharts) {
                result.push(
                    getAllEventsRegionsConfig('ALL_EVENTS_REGIONS', 3, this.regions, this.courseStatistics) as Highcharts.Options,
                    getEndedEventsRegionsConfig('ENDED_EVENTS_REGIONS', 3, this.regions, this.endedCourseStatistics) as Highcharts.Options
                );
            }

            return result;
        },
    },
});
